import { type ReactNode, useLayoutEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/store/auth';
import { useMe } from '@/api/queries/user';
import { usePassport } from '@/api/queries/passport';
import { authToken } from '@/api/auth-token';
import { paths } from './paths';
import { Spinner } from '@/components/ui/Spinner';

export function RequireAuth({ children }: { children: ReactNode }): JSX.Element {
  const hasToken = useAuthStore((s) => s.hasToken);
  const location = useLocation();

  if (!hasToken) {
    return <Navigate to={paths.authLanding} replace state={{ from: location.pathname }} />;
  }
  return <>{children}</>;
}

// Mobile WebView opens /pay/:intentId without SPA cookies — Flutter passes
// ?access_token= so we can call GET /payments/intents/{id} before TipTop widget.
export function PayIntentEntry({ children }: { children: ReactNode }): JSX.Element {
  const location = useLocation();
  const urlToken = new URLSearchParams(location.search).get('access_token');

  useLayoutEffect(() => {
    if (urlToken) {
      authToken.write({ accessToken: urlToken });
      useAuthStore.getState().setHasToken(true);
    }
  }, [urlToken]);

  const hasToken = useAuthStore((s) => s.hasToken);

  if (urlToken && !authToken.read()?.accessToken) {
    return (
      <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}>
        <Spinner />
      </div>
    );
  }

  if (!hasToken && !urlToken) {
    return <Navigate to={paths.authLanding} replace state={{ from: location.pathname }} />;
  }

  return <>{children}</>;
}

export function RequireOnboarding({ children }: { children: ReactNode }): JSX.Element {
  const me = useMe();
  const passport = usePassport();

  if (me.isLoading || passport.isLoading) {
    return (
      <div style={{ minHeight: '60vh', display: 'grid', placeItems: 'center' }}>
        <Spinner />
      </div>
    );
  }
  if (!me.data) {
    return <Navigate to={paths.authLanding} replace />;
  }
  if (!passport.data) {
    return <Navigate to={paths.onboardingProfile} replace />;
  }
  return <>{children}</>;
}

export function RequireEmployerAdmin({ children }: { children: ReactNode }): JSX.Element {
  const me = useMe();
  if (me.isLoading) {
    return (
      <div style={{ minHeight: '60vh', display: 'grid', placeItems: 'center' }}>
        <Spinner />
      </div>
    );
  }
  if (me.data?.role !== 'employer_admin') {
    return <Navigate to={paths.business} replace />;
  }
  return <>{children}</>;
}
